import { z } from 'zod/v4';
import {
  DEFAULT_PAGE_SIZE,
  FILTER_MAX_LENGTH,
  MAX_PAGE_SIZE,
} from '../constants.js';
import { QuestionnaireListRowSchema } from './questionnaire-list.js';

/**
 * Sort columns the admin template list accepts — mirrors `SORT_COLUMNS_ENUM` in
 * the legacy `routes/schemas/v1/request/templates.js`, which is a *narrower* set
 * than the tenant questionnaire list advertises. Every key here maps to a SQL
 * expression in `core/db/questionnaires/filters.ts` (`SORT_COLUMNS`).
 */
export const AdminQuestionnaireTemplatesListSortColumns = [
  'name',
  'createdAt',
  'updatedAt',
  'questionCount',
  'timesUsedAsTemplate',
  'isActive',
] as const;

export type AdminQuestionnaireTemplatesListSortColumn =
  (typeof AdminQuestionnaireTemplatesListSortColumns)[number];

/**
 * `column:DIRECTION` pairs, comma-separated, built from the column tuple above
 * so the two cannot drift.
 *
 * Deliberately stricter than the tenant list's `/^[a-zA-Z]+:(ASC|DESC)/` — the
 * legacy admin route's JSON-Schema `pattern` enumerated its six columns, so an
 * unmapped column answered **400**. A permissive pattern would let it through to
 * `buildOrderBy`, which silently drops it and falls back to the default sort:
 * a 400 quietly becoming a 200.
 */
const sortParamSchema = z
  .string()
  .regex(
    new RegExp(
      `^((${AdminQuestionnaireTemplatesListSortColumns.join('|')}):(ASC|DESC))(,(${AdminQuestionnaireTemplatesListSortColumns.join('|')}):(ASC|DESC))*$`,
    ),
  )
  .optional();

/**
 * Fastify coerces `?flag=true` into a real boolean, but callers building the
 * query string with `URLSearchParams` send the literal string. Accept both,
 * matching how the legacy JSON-Schema `boolean` behaved in practice.
 */
const booleanLike = z
  .union([z.boolean(), z.string()])
  .optional()
  .transform(v => v === true || v === 'true');

/**
 * The legacy querystring also accepted `isTemplate`, but its handler never read
 * it — `is_template = 1` is hardcoded in both SQL variants — so it is dropped
 * here rather than carried forward as accepted-but-ignored noise.
 *
 * Unknown keys are stripped rather than rejected. The legacy schema was
 * `additionalProperties: false`, so this is strictly more permissive; it matters
 * because the admin table's call site spreads `...queryParams`
 * (`supplierCompanyIds`, `productLevelId`), vestigial on that page but on the wire
 * the moment they appear in the URL.
 */
export const AdminQuestionnaireTemplatesListQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  sort: sortParamSchema,
  filter: z.string().max(FILTER_MAX_LENGTH).optional(),
  myTemplates: booleanLike,
});

export type AdminQuestionnaireTemplatesListQuery = z.infer<
  typeof AdminQuestionnaireTemplatesListQuerySchema
>;

/**
 * Row shape is identical to the tenant template list — both endpoints run the
 * same `listQuestionnaireTemplatesDb` query — so the row schema is reused rather
 * than duplicated. It is a `looseObject`, which is what lets the audit columns
 * (`updatedByUserName`, `updatedByCompanyName`) and `isActive` /
 * `timesUsedAsTemplate` through to the admin table undeclared.
 */
export const AdminQuestionnaireTemplateListRowSchema =
  QuestionnaireListRowSchema;

/**
 * The rows of the Retraced templates list.
 *
 * Just the array — the `{metadata, data}` envelope around it comes from
 * `buildResponseSchemas({ 200: { type: 'data', schema, paginated: true } })` on the
 * route, the same builder every other paginated route on the platform uses. Nothing
 * here describes the envelope, so there is one definition of it rather than six.
 */
export const AdminQuestionnaireTemplatesListResponseSchema = z.array(
  AdminQuestionnaireTemplateListRowSchema,
);

export type AdminQuestionnaireTemplatesListResponse = z.infer<
  typeof AdminQuestionnaireTemplatesListResponseSchema
>;
